import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { EditActions, editActions } from "../../actions/editActions";
import { createEmptyUser, createEmptyAADGroup } from "../../utils/pdApiUtils";
import {
  editedObjectIdSelector,
  isEditActiveSelector,
  editedObjectIdentifierSelector
} from "../../reducers/editReducer";
import { UsersTable } from "./UsersTable";
import { ActionMenu } from "../common/ActionMenu/ActionMenu";
import NavigationLink from "../../components/NavigationLink/NavigationLink";
import ContentLayout from "../../components/ContentLayout/ContentLayout";
import { AADGroupsTable } from "./AADGroups/AADGroups";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import { ColumnActions, columnActions } from "../../actions/columnActions";
import { Filter } from "../../components/Table/Migration/LegacyTypes";
import { mainActions } from "../../actions/mainActions";

type StateProps = {
  columns: State.ColumnMap;
  pdApi: State.PdApi | undefined;
  mainState: State.Main;
  editedObjectId: number | undefined;
  isEditActive: boolean;
  userAccount: string;
  objectIdentifier: string;
  filterOptions: State.FilterOptions;
};

type DispatchProps = {
  // Connected actions
  editActions: typeof editActions;
  columnActions: typeof columnActions;
  mainActions: typeof mainActions;
};

/* Containers own state */
type UsersState = {
  isModalOpen: boolean;
  tableColumnFilters: {
    [key in State.UserSubmenu]: Filter[];
  };};

type OwnProps = {
  subMenuRoute: State.UserSubmenu;
};
export type UsersProps = StateProps & DispatchProps & OwnProps;

export class Users extends React.Component<UsersProps, UsersState> {
  state = { 
    isModalOpen: false,
    tableColumnFilters: {
      "Users": [],
      "AADGroups": []
    }
  };
  subPages: Internal.SubPages = {
    Users: {
      itemName: "user",
      PdApiField: "users",
      dataView: UsersTable,
      createEmpty: createEmptyUser,
      setExtraProperties: null
    },
    AADGroups: {
      itemName: "aadgroup",
      PdApiField: "aadgroups",
      dataView: AADGroupsTable,
      createEmpty: createEmptyAADGroup,
      setExtraProperties: null
    }
  };
  render(): JSX.Element {
    const { subMenuRoute } = this.props;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const submenuLinks = Object.keys(this.subPages).map(
      (subPageName: Api.AnyItem): JSX.Element => this.buildLink(subPageName)
    );
    const actionMenu = this.getActionMenu(subMenuRoute);
    const dataView = this.getDataView(subMenuRoute);
    const modal = this.buildModal();
    return (
      <>
        <ContentLayout submenu={submenuLinks} actionMenu={actionMenu} dataView={dataView} />
        {modal}
      </>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private setColumnFilters = (subMenuRoute: State.UserSubmenu, newFilters: Filter[]): any => {
    const newFilterSet = { ...this.state.tableColumnFilters, [subMenuRoute]: newFilters };
    this.setState(prevState => ({
      ...prevState,
      tableColumnFilters: newFilterSet
    }));
  };

  private getDataView = (subMenuRoute: State.UserSubmenu): JSX.Element => {
    if (subMenuRoute in this.subPages) {
      const passProps = { 
        ...this.props, 
        toggleModal: this.toggleModal,
        setColumnFilters: this.setColumnFilters
      };
      return React.createElement(this.subPages[subMenuRoute].dataView, passProps);
    }
    return <>Error</>;
  };

  private getActionMenu = (subMenuRoute: State.UserSubmenu): JSX.Element | null => (
    <ActionMenu
      {...this.props}
      itemName={this.subPages[subMenuRoute].itemName}
      selectedColumnsName={subMenuRoute}
      dataFieldName={this.subPages[subMenuRoute].PdApiField}
      setExtraProperties={this.subPages[subMenuRoute].setExtraProperties}
      createEmpty={this.subPages[subMenuRoute].createEmpty}
      columnFilters={this.state.tableColumnFilters[subMenuRoute]}
      />
  );

  private buildLink = (subPageName: State.UserSubmenu): JSX.Element => {
    // To dynamically build urls we use the itenName and make it plural, so -s || -y => -ies
    let parsedLink = this.subPages[subPageName].itemName.replace(" ", "-");
    parsedLink = parsedLink.slice(-1) === "y" ? parsedLink.slice(0, -1) + "ies" : parsedLink + "s";
    return (
      <NavigationLink
        key={subPageName}
        handleOnClick={this.props.editActions.cancelEditing}
        name={subPageName}
        to={"/users/" + parsedLink + "/"}
      />
    );
  };

  private buildModal = () => {
    const content = (
      <span>
        Are you sure you want to delete this {this.subPages[this.props.subMenuRoute].itemName}?
        <br />
        {this.props.objectIdentifier}
      </span>
    );

    return (
      <ConfirmationModal
        isOpen={this.state.isModalOpen}
        size="mini"
        header="Confirm delete"
        content={content}
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={() =>
          this.props.editActions.delete(
            this.subPages[this.props.subMenuRoute].PdApiField as Api.OptionsGrud
          )
        }
        onCancel={this.toggleModal}
      />
    );
  };

  /*
   * Toggles the modal based on previous state.
   */
  private toggleModal = (): void => {
    this.setState(prevState => ({
      ...prevState,
      isModalOpen: !prevState.isModalOpen
    }));
  };
}

const mapStateToProps = (state: State.Root, ownProps: OwnProps) => ({
  columns: state.columns,
  pdApi: state.pdApi,
  mainState: state.main,
  isEditActive: isEditActiveSelector(state.editView),
  editedObjectId: editedObjectIdSelector(state.editView),
  userAccount: editedObjectIdentifierSelector(state.editView, "Users"),
  objectIdentifier: editedObjectIdentifierSelector(state.editView, ownProps.subMenuRoute),
  filterOptions: state.filterOptions
});

const mapDispatchToProps = (dispatch: Dispatch<ColumnActions | EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch),
  columnActions: bindActionCreators({ ...columnActions }, dispatch),
  mainActions: bindActionCreators({ ...mainActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Users);
